// Utilities to build and parse unique organization route identifiers
// Goal: allow multiple pages for the same organization (different locations)
// to have unique, stable URLs.

// Simple stable hash for short suffix (not crypto). Returns 6-char base36.
function shortHash(input) {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0; // 32-bit
  }
  const u = Math.abs(h);
  return u.toString(36).slice(0, 6);
}
function slugify(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

// Build a unique route id: `${slugOrId}&${friendly-location}` when needed
// If no location metadata present or no name collision, returns just slugOrId.
export function buildOrganizationRouteId(org, options) {
  const base = String(org.slug || org.id || "").trim();
  if (!base) return "";
  if (options?.baseOnly) return base;
  const loc = org.infoSection?.location || {};
  // Prefer state/city for friendly text (requested), fallback to city only
  const friendlyParts = [loc.state, loc.city].filter(Boolean);
  const friendly = slugify(friendlyParts.join("-"));
  // Determine if this new card collides on name with existing collection
  const isEdit = Boolean(org.isEdit);
  let hasNameCollision = false;
  let hasSlugCollision = false;
  try {
    const name = String(org.orgName || "").trim().toLowerCase();
    const thisBase = String(org.slug || org.id || "");
    if (name && Array.isArray(options?.existing)) {
      // Name collision: any other entry (by reference) with the same normalized name
      hasNameCollision = options.existing.some(e => {
        if (!e || e === org) return false;
        const n = String(e.orgName || "").trim().toLowerCase();
        if (!n) return false;
        return n === name;
      });
      // Slug/id base collision: any other entry (by reference) with the same base id/slug
      hasSlugCollision = options.existing.some(e => {
        if (!e || e === org) return false;
        const sid = String(e.slug || e.id || "");
        return !!sid && !!thisBase && sid === thisBase;
      });
    }
  } catch {}
  if (isEdit || !hasNameCollision && !hasSlugCollision) {
    // If there's no name collision, keep base route
    return base;
  }
  // If we have location info, append a friendly suffix using '&'
  if (friendly) {
    const candidate = `${base}&${friendly}`;
    // If another entry with same base and same friendly exists, append a disambiguating hash
    const hasFriendlyCollision = Array.isArray(options?.existing) ? options.existing.some(e => {
      if (!e || e === org) return false;
      const eb = String(e.slug || e.id || "");
      const eloc = e.infoSection?.location || {};
      const efriendly = slugify([eloc.state, eloc.city].filter(Boolean).join("-"));
      return eb === base && efriendly === friendly;
    }) : false;
    if (!hasFriendlyCollision) return candidate;
    const h = shortHash(JSON.stringify(org));
    return `${candidate}-${h.slice(0, 4)}`;
  }
  // No location provided but name collides: append a short hash to keep routes unique
  const nameKey = String(org.orgName || "").trim().toLowerCase();
  const suffix = shortHash(`${nameKey}:${org.id || org.slug || Math.random()}`);
  return `${base}&${suffix}`;
}

// Decode a route id back to its base identifier (slug or id).
export function decodeOrganizationRouteParam(routeParam) {
  const raw = String(routeParam || "");
  // Prefer new '&' delimiter; keep legacy '--' for backward compatibility
  let idx = raw.indexOf("&");
  let offset = 1;
  if (idx === -1) {
    idx = raw.indexOf("--");
    offset = 2;
  }
  if (idx === -1) return {
    baseIdOrSlug: raw
  };
  return {
    baseIdOrSlug: raw.slice(0, idx),
    locationKey: raw.slice(idx + offset) || undefined
  };
}